/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import React, { Fragment, useState } from 'react'
import { find, last, split } from 'lodash'

import {
  EuiBadge,
  EuiButtonEmpty,
  EuiCode,
  EuiDescribedFormGroup,
  EuiFormRow,
  EuiSkeletonText,
  EuiSpacer,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { PlatformInfo } from '@modules/cloud-api/v1/types'
import { CuiTable } from '@modules/cui/Table'
import { CuiLink } from '@modules/cui/Link'

import { hostUrl } from '../../../lib/urlBuilder'

interface Props {
  regionId: string
  platform: PlatformInfo | null
}

function getHash(image) {
  return last(split(image.hash, `:`))
}

const PlatformAdminServersStatus: React.FC<Props> = ({ regionId, platform }) => {
  const [expanded, setExpanded] = useState(false)

  const renderStatusSwitch = () => {
    if (expanded) {
      return (
        <EuiButtonEmpty
          data-test-id='admin-api-server-hide-details'
          iconSide='right'
          iconType='arrowUp'
          onClick={() => setExpanded(false)}
        >
          <FormattedMessage
            id='region-admin-platform.hide-system-details'
            defaultMessage='Hide system details'
          />
        </EuiButtonEmpty>
      )
    }

    return (
      <EuiButtonEmpty
        data-test-id='admin-api-server-show-details'
        iconSide='right'
        iconType='arrowDown'
        onClick={() => setExpanded(true)}
      >
        <FormattedMessage
          id='region-admin-platform.view-system-details'
          defaultMessage='View system details'
        />
      </EuiButtonEmpty>
    )
  }

  const renderStatusTable = () => {
    if (!expanded) {
      return null
    }

    if (!platform) {
      return (
        <Fragment>
          <EuiSpacer size='l' />

          <EuiSkeletonText />
        </Fragment>
      )
    }

    const adminConsole = find(platform.services, { type: `admin-console` })

    const columns = [
      {
        label: <FormattedMessage id='region-admin-platform.host' defaultMessage='Host' />,
        render: (image) => <CuiLink to={hostUrl(regionId, image.id)}>{image.id}</CuiLink>,
        sortKey: `id`,
        width: `200px`,
      },
      {
        label: <FormattedMessage id='region-admin-platform.version' defaultMessage='Version' />,
        render: (image) => <EuiBadge color='hollow'>{image.version}</EuiBadge>,
        sortKey: `version`,
      },
      {
        className: 'regionAdmin-buildSha',
        label: <FormattedMessage id='region-admin-platform.build-sha' defaultMessage='Build SHA' />,
        render: (image) => <EuiCode data-test-id='build-sha'>{getHash(image)}</EuiCode>,
        sortKey: getHash,
      },
    ]

    return (
      <Fragment>
        <EuiSpacer size='l' />

        <CuiTable
          css={css`
            .regionAdmin-buildSha {
              word-break: break-word;
            }
          `}
          rows={adminConsole ? adminConsole.image : []}
          columns={columns}
          getRowId={(image) => image.id}
        />
      </Fragment>
    )
  }

  return (
    <Fragment>
      <EuiDescribedFormGroup
        title={
          <EuiTitle size='xs'>
            <h3>
              <FormattedMessage
                id='region-admin-platform.admin-api-server-status'
                defaultMessage='Admin API Server'
              />
            </h3>
          </EuiTitle>
        }
        description={
          <FormattedMessage
            id='region-admin-platform.admin-api-server-description'
            defaultMessage='Check the status of the API server hosts.'
          />
        }
        fullWidth={true}
      >
        <EuiFormRow fullWidth={true}>{renderStatusSwitch()}</EuiFormRow>
      </EuiDescribedFormGroup>

      {renderStatusTable()}
    </Fragment>
  )
}

export default PlatformAdminServersStatus
